import React from 'react';
import { Filter, X } from 'lucide-react';
import { SearchFilters as SearchFiltersType, Route } from '../types';
import SearchInput from './SearchInput';
import RegionCragFilter from './RegionCragFilter';

interface SearchFiltersProps {
  searchTerm: string;
  onSearchChange: (term: string) => void;
  filters: SearchFiltersType;
  onFiltersChange: (filters: Partial<SearchFiltersType>) => void;
  onClearFilters: () => void;
  routes: Route[];
}

const GRADE_OPTIONS = [
  '5.0', '5.1', '5.2', '5.3', '5.4', '5.5', '5.6', '5.7', '5.8', '5.9',
  '5.10a', '5.10b', '5.10c', '5.10d',
  '5.11a', '5.11b', '5.11c', '5.11d',
  '5.12a', '5.12b', '5.12c', '5.12d',
  '5.13a', '5.13b', '5.13c', '5.13d',
  '5.14a', '5.14b', '5.14c', '5.14d',
  '5.15a', '5.15b', '5.15c', '5.15d',
  'V0', 'V1', 'V2', 'V3', 'V4', 'V5', 'V6', 'V7', 'V8', 'V9', 'V10',
  'V11', 'V12', 'V13', 'V14', 'V15', 'V16', 'V17'
];

export default function SearchFilters({
  searchTerm,
  onSearchChange,
  filters,
  onFiltersChange,
  onClearFilters,
  routes,
}: SearchFiltersProps) {
  const hasActiveFilters = Object.values(filters).some(value => 
    value !== undefined && value !== '' && value !== false
  );

  return (
    <div className="card p-4 mb-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-800 flex items-center">
          <Filter className="w-5 h-5 mr-2" />
          Search & Filter Routes
        </h2>
        {hasActiveFilters && (
          <button
            onClick={onClearFilters}
            className="btn-secondary text-sm flex items-center"
          >
            <X className="w-4 h-4 mr-1" />
            Clear All
          </button>
        )}
      </div>

      {/* Region and Crag Filters */}
      <div className="mb-6">
        <RegionCragFilter
          routes={routes}
          filters={filters}
          onFiltersChange={onFiltersChange}
        />
      </div>

      {/* Filters Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Length Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Length (ft)
          </label>
          <div className="flex gap-2">
            <input
              type="number"
              placeholder="Min"
              value={filters.minLength || ''}
              onChange={(e) => onFiltersChange({ 
                minLength: e.target.value ? parseInt(e.target.value) : undefined 
              })}
              className="input-field flex-1"
            />
            <input
              type="number"
              placeholder="Max"
              value={filters.maxLength || ''}
              onChange={(e) => onFiltersChange({ 
                maxLength: e.target.value ? parseInt(e.target.value) : undefined 
              })}
              className="input-field flex-1"
            />
          </div>
        </div>

        {/* Quality Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Min Quality (stars)
          </label>
          <select
            value={filters.minQuality || ''}
            onChange={(e) => onFiltersChange({ 
              minQuality: e.target.value ? parseFloat(e.target.value) : undefined 
            })}
            className="input-field w-full"
          >
            <option value="">Any Quality</option>
            {[2, 2.5, 3, 3.5].map(star => (
              <option key={star} value={star}>{star} or higher</option>
            ))}
          </select>
        </div>

        {/* Grade Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Grade Range
          </label>
          <div className="flex gap-2">
            <select
              value={filters.minGrade || ''}
              onChange={(e) => onFiltersChange({ minGrade: e.target.value || undefined })}
              className="input-field flex-1"
            >
              <option value="">Min Grade</option>
              {GRADE_OPTIONS.map(grade => (
                <option key={grade} value={grade}>{grade}</option>
              ))}
            </select>
            <select
              value={filters.maxGrade || ''}
              onChange={(e) => onFiltersChange({ maxGrade: e.target.value || undefined })}
              className="input-field flex-1"
            >
              <option value="">Max Grade</option>
              {GRADE_OPTIONS.map(grade => (
                <option key={grade} value={grade}>{grade}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Route Type Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Route Type
          </label>
          <div className="flex gap-4">
            <label className="flex items-center">
              <input
                type="radio"
                name="routeType"
                checked={!filters.trad && !filters.sport && !filters.boulder}
                onChange={() => onFiltersChange({ trad: undefined, sport: undefined, boulder: undefined })}
                className="mr-2"
              />
              All
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="routeType"
                checked={filters.trad === true}
                onChange={() => onFiltersChange({ trad: true, sport: false, boulder: false })}
                className="mr-2"
              />
              Trad
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="routeType"
                checked={filters.sport === true}
                onChange={() => onFiltersChange({ trad: false, sport: true, boulder: false })}
                className="mr-2"
              />
              Sport
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="routeType"
                checked={filters.boulder === true}
                onChange={() => onFiltersChange({ trad: false, sport: false, boulder: true })}
                className="mr-2"
              />
              Boulder
            </label>
          </div>
        </div>

        {/* Pitches Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Pitches
          </label>
          <div className="flex gap-2">
            <select
              value={filters.minPitches || ''}
              onChange={(e) => onFiltersChange({ 
                minPitches: e.target.value ? parseInt(e.target.value) : undefined 
              })}
              className="input-field flex-1"
            >
              <option value="">Min</option>
              {[1, 2, 3, 4, 5].map(pitch => (
                <option key={pitch} value={pitch}>{pitch}</option>
              ))}
            </select>
            <select
              value={filters.maxPitches || ''}
              onChange={(e) => onFiltersChange({ 
                maxPitches: e.target.value ? parseInt(e.target.value) : undefined 
              })}
              className="input-field flex-1"
            >
              <option value="">Max</option>
              {[1, 2, 3, 4, 5].map(pitch => (
                <option key={pitch} value={pitch}>{pitch}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Search Input with Typeahead - moved to bottom */}
      <div className="mt-6">
        <SearchInput
          searchTerm={searchTerm}
          onSearchChange={onSearchChange}
          routes={routes}
          className="input-field"
        />
      </div>
    </div>
  );
}
